/**
 * dt-button - Button component with variants and states
 * Variants: primary, secondary, success, outline, ghost
 * States: disabled, loading
 */
import { BaseElement } from '../base-element';

export class DtButton extends BaseElement {
  static observedAttributes = ['variant', 'disabled', 'loading', 'type'];

  private _variant: 'primary' | 'secondary' | 'success' | 'outline' | 'ghost' = 'primary';
  private _disabled = false;
  private _loading = false;
  private _type: 'button' | 'submit' = 'button';
  private _content = '';
  private _contentCaptured = false;

  get variant(): string {
    return this._variant;
  }

  set variant(value: string) {
    if (['primary', 'secondary', 'success', 'outline', 'ghost'].includes(value)) {
      this._variant = value as 'primary' | 'secondary' | 'success' | 'outline' | 'ghost';
      this.updateRender();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = Boolean(value);
    this.updateRender();
  }

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = Boolean(value);
    this.updateRender();
  }

  get type(): string {
    return this._type;
  }

  set type(value: string) {
    if (value === 'button' || value === 'submit') {
      this._type = value;
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.captureContent();
    this.updateRender();
    this.addEventListener('click', this.handleClick, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick, true);
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'variant':
        this.variant = newValue;
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        this.updateRender();
        break;
      case 'loading':
        this._loading = newValue !== null && newValue !== 'false';
        this.updateRender();
        break;
      case 'type':
        this.type = newValue;
        break;
    }
  }

  private handleClick = (e: Event): void => {
    if (this._disabled || this._loading) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  private captureContent(): void {
    if (!this._contentCaptured) {
      this._content = this.innerHTML.trim();
      this._contentCaptured = true;
    }
  }

  private getVariantClasses(): string {
    const base =
      'inline-flex items-center justify-center rounded-lg h-12 px-6 text-base font-semibold leading-normal tracking-[0.015em] transition-all focus:outline-hidden focus:ring-4 active:scale-[0.98]';

    if (this._disabled) {
      const outlineBorderClass = this._variant === 'outline' ? 'border border-neutral-300' : '';
      return `${base} bg-neutral-200 text-neutral-500 ${outlineBorderClass} shadow-none`;
    }

    const variants: Record<string, string> = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500/30 shadow-xs',
      secondary:
        'bg-primary-500/10 text-primary-600 hover:bg-primary-500/20 focus:ring-primary-500/10',
      success:
        'bg-success-500 text-white hover:bg-success-600 focus:ring-success-500/30 shadow-xs',
      outline:
        'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 focus:ring-neutral-200',
      ghost:
        'bg-transparent text-primary-600 hover:bg-neutral-100 focus:ring-neutral-100',
    };

    return `${base} ${variants[this._variant]}`;
  }

  private getStateClasses(): string {
    if (this._disabled) {
      return 'cursor-not-allowed';
    }
    if (this._loading) {
      return 'cursor-wait opacity-90 pointer-events-none';
    }
    return 'cursor-pointer';
  }

  private renderSpinner(): string {
    return `
      <svg class="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
      </svg>
    `;
  }

  private updateRender(): void {
    this.captureContent();
    const variantClasses = this.getVariantClasses();
    const stateClasses = this.getStateClasses();
    const isUnavailable = this._disabled || this._loading;

    this.render(`
      <button 
        type="${this._type}"
        class="${variantClasses} ${stateClasses}"
        ${isUnavailable ? 'disabled aria-disabled="true"' : ''}
        ${this._loading ? 'aria-busy="true"' : ''}
      >
        ${this._loading ? this.renderSpinner() : ''}
        <span class="truncate">${this._content}</span>
      </button>
    `);
  }
}

customElements.define('dt-button', DtButton);
